import * as Sentry from '@sentry/vue'
import * as types from './user.types'

import Auth from '@/services/Auth'
import { PROFILE_PROGRESS_THRESHOLD } from '@/services/startup-report'
import { getNewAppUrl } from '@/utils/urls'
import http from '@/services/http'
import { notify } from '@/services/notifications'
import router from '@/routes'
import { sleep } from '@/utils'

export default {
  namespaced: true,
  state: {
    fetchInProgress: false,
    alreadyFetched: false,
    isAuthorized: false,
    hasAuthToken: Auth.hasAuthToken(),
    data: initEmptyUser(),
    defaultRoute: {},
  },
  getters: {
    dashboardRoute: (state) => getRouteForRole(state.data.role),
  },
  mutations: {
    [types.START_FETCH](state) {
      state.fetchInProgress = true
    },
    [types.AUTHORIZED](state, data) {
      state.alreadyFetched = true
      state.isAuthorized = true
      state.data = data
      const user = state.data
      window.user = user

      user.isAdmin = data.role === 'ADMIN'
      user.isInvestor = data.role === 'INVESTOR'
      user.isOrganizer = data.role === 'ORGANIZER'
      user.isStartup = data.role === 'STARTUP'

      user.isInvestorOrOrganizer = user.isInvestor || user.isOrganizer

      if (user.isStartup) {
        if (!user.startups.length) {
          throw new Error(
            `User ${user.id} has startup role and no owned startups`,
          )
        }
        user.startupId = user.startups[0].id
        user.startup = user.startups[0]
      } else {
        user.startupId = null
        user.startup = null
      }
      user.isOwner = (startupId) => startupId === user.startupId

      user.canSeeStartupList =
        user.isAdmin || user.isOrganizer || user.isInvestor

      if (user.isOrganizer) {
        user.organizationId = user.organizations[0].id
        user.hasOrganizationDemoModeEnabled = user.organizations.some(
          (org) => org.has_demo_mode_enabled,
        )
      } else {
        user.organizationId = null
      }
    },
    [types.UNAUTHORIZED](state) {
      state.data = initEmptyUser()
      state.isAuthorized = false
      state.alreadyFetched = false
      Sentry.setUser(null)
    },
    [types.STOP_FETCH](state) {
      state.fetchInProgress = false
    },
    setData(state, newData) {
      state.data = { ...state.data, ...newData }
    },
  },
  actions: {
    async fetchNoRedirect({ commit, state }, opts) {
      opts = opts || {}
      return fetchUser({ commit, state }, opts)
    },
    async refetchUser({ commit, state, dispatch }) {
      return await dispatch('getUser', { force: true })
    },
    async getUser({ commit, state }, opts) {
      opts = opts || { force: false }
      if (state.alreadyFetched && !opts.force) {
        return state.data
      } else {
        return await fetchUser({ commit, state })
      }
    },
    async fetch({ commit, state }, opts) {
      opts = opts || {}
      const user = await fetchUser({ commit, state }, opts)
      if (!user) {
        const newAppUrl = router.resolve({
          name: 'login',
          query: { next: router.currentRoute.fullPath },
        }).href
        window.location.href = getNewAppUrl(newAppUrl)
      }
      return user
    },
    logout({ commit }) {
      commit(types.UNAUTHORIZED)
      Auth.logout()
      notify({ type: 'info', title: 'Logged out successfully' })
    },
    goToDefaultPage({ state }) {
      if (!state.data || !state.data.role) {
        throw new Error('Cannot goToDefaultPage when user was not fetched')
      }
      let isNewAppRoute = false
      let route = getRouteForRole(state.data.role)
      if (route.name === 'dashboardStartupList') {
        isNewAppRoute = true
      }
      const hasDraftApplicationAndRequiresProfile =
        getHasDraftApplicationAndRequiresProfile(state)
      if (hasDraftApplicationAndRequiresProfile) {
        route = { name: 'dashboard' }
      } else if (
        state.data.isStartup &&
        state.data.startups.length &&
        state.data.startups[0].applications.length
      ) {
        const applicationId = state.data.startups[0].applications[0].id
        route = {
          name: 'dashboardApplicationDetails',
          params: { id: applicationId },
        }
        isNewAppRoute = true
      }
      if (state.data && state.data.isOrganizer && state.data.self_registered) {
        route = { name: 'dashboardFeaturedStartups' }
      }
      if (state.data && state.data.require_to_set_password) {
        route = { name: 'setNewPasswordPage' }
      }
      if (route) {
        if (route.name === 'dashboard') {
          isNewAppRoute = true
        }
        if (isNewAppRoute) {
          const newAppUrl = router.resolve(route).href
          window.location.href = getNewAppUrl(newAppUrl)
        } else {
          router.push(route)
        }
      } else if (state) {
        console.log(' => no default route for the role:', state.data.role)
      } else {
        console.log(' => no state available')
      }
    },
  },
}

function getHasDraftApplicationAndRequiresProfile(state) {
  if (state.data && state.data.startup) {
    const applications = state.data.startup.applications
    const drafts = applications.filter(
      (a) => a.program_type !== 'FORM' && a.state === 'draft',
    )
    const hasCompletedProfile =
      state.data.startup.profile_progress >= PROFILE_PROGRESS_THRESHOLD
    return drafts.length && !hasCompletedProfile
  }
  return false
}

function getRouteForRole(role) {
  const routeName = {
    ORGANIZER: 'dashboardStartupList',
    INVESTOR: 'dashboardStartupList',
    STARTUP: 'dashboard',
    ADMIN: 'admin',
  }[role]
  if (routeName) {
    return { name: routeName }
  } else {
    console.log(' => no default route for the role:', role)
    return 'dashboard'
  }
}

let fetchUserRequest = null

async function fetchUser({ commit, state }, opts) {
  if (!Auth.hasAuthToken()) return
  commit(types.START_FETCH)
  opts = opts || {}
  if (!fetchUserRequest) {
    fetchUserRequest = http.getAuthed('/auth/me')
  }
  const request = fetchUserRequest
  try {
    if (opts.sleep) {
      await sleep(opts.sleep)
    }
    const response = await request
    commit(types.AUTHORIZED, response.data)
    if (opts.callback) {
      opts.callback()
    }
    return state.data
  } catch (error) {
    console.error(error)
    commit(types.UNAUTHORIZED)
    return null
  } finally {
    commit(types.STOP_FETCH)
    fetchUserRequest = null
  }
}

function initEmptyUser() {
  return {
    events: [],
    startups: [],
    organizations: [],
    isOwner: (startupId) => false,
    isAdmin: false,
    features: [],
    permissions: [],
    accepted_terms_at: null,
  }
}
